import {
  getBasicOffers,
  getOfferId,
  getBmi,
  getDaysTillGoalDate,
  getFasterProgressDaysDiff,
  getLocalizedDateLabel,
  getVariantPageUrl,
  kgToLbs,
  isWeightGain,
  getProjectedWeightLoss,
  getOfferWeeks,
  isPipeWeekTrial,
  getOfferMonths,
} from "../helpers"
import { createPrimerCheckoutSession } from "../payments"

// Alpine JS component that provides the basic plan checkout functionality,
// which is common or shared throughout different brands to stay DRY.
export default ({
  planSelectionPath,
  paymentsPath,
  isTrackVisitCheckout,
  isAbandonedBasket,
} = {}) => ({
  offers: [],
  activeOffer: null,
  isLoading: false,

  //
  // Core
  //

  init() {
    this.commonInit()
  },

  // Basic initialization to use in customized/extended views
  commonInit() {
    this.store = Alpine.store("data")

    window.addEventListener("pageshow", (event) => {
      // If page loaded from cache
      if (event.persisted) {
        // Reload page to apply the recent updates instead of old cached ones
        window.location.reload()
      }
    })

    this.initOffers()

    // Track if payments view
    if (isTrackVisitCheckout) {
      this.trackVisitCheckout()
    }

    // Push updated data to the BE in case if this is AC plan selection page
    if (isAbandonedBasket) {
      this.updateOrderUtms()
    }

    // Init Klaviyo user and track page, cause u can start tracking after init only. Not to miss any events ^^
    this.store.internalTracker.isUserIdentified({}, "klaviyo").then((isIdentified) => {
      if (!isIdentified) {
        this.store.internalTracker.trackIdentify({
          customData: { email: this.store.values.email },
          destinationService: "klaviyo",
          callBackFn: () => {
            this.store.internalTracker.trackPageView({}, "klaviyo")
          },
        })
      }
    })
  },

  initOffers() {
    // Restore purchased offer, cause active offer is overwritten during upsell
    this.store.restorePurchasedBasicOffer()

    const offers = getBasicOffers(pipelineConfig.offers)
    this.offers = offers

    let storedOffer = this.store.values.activeOffer
    const isStoredOfferBasic = storedOffer && storedOffer.type.toLowerCase() === "basic"

    // Active offer should always be basic for this view
    if (!storedOffer || !isStoredOfferBasic) {
      storedOffer = offers.find((offer) => offer.customData.default_select === "true")
    }

    this.activeOffer = storedOffer || offers[0]
    this.store.values.activeOffer = this.activeOffer
  },

  selectOffer(offer) {
    this.store.internalTracker.trackClick({ clickName: "Plan Selected" })

    this.activeOffer = offer
    this.store.values.activeOffer = offer
  },

  submitOffer() {
    this.store.internalTracker.setEventToSessionStorage({ clickName: "Get Plan Clicked" })
    this.commonSubmitOffer()
  },

  // Basic submit to use in customized/extended views
  async commonSubmitOffer({ isPreloadCheckout } = {}) {
    if (!isPreloadCheckout) {
      this.goToPayments()
      return
    }

    // Flow, used for pipes with checkout preload on plan selection step
    this.isLoading = true

    if (isPreloadCheckout) {
      try {
        this.store.globalError = null

        const checkoutSession = await createPrimerCheckoutSession({ restrictSaveCard: true })

        this.store.storeCheckoutData({ checkoutSession, isPreloaded: true })

        this.goToPayments()
        this.isLoading = false
      } catch (error) {
        console.error("Order submit failed:", error)
        this.isLoading = false
        this.store.globalError = error
      }
    }
  },

  goToPlanSelection() {
    if (!planSelectionPath) return
    window.location = getVariantPageUrl({ pagePath: planSelectionPath, pipelineConfig })
  },

  goToPayments() {
    window.location = getVariantPageUrl({ pagePath: paymentsPath, pipelineConfig })
  },

  async trackVisitCheckout() {
    this.store.values.status = "VISIT_CHECKOUT"
    await this.store.push()
  },

  async updateOrderUtms() {
    await this.store.push()
  },

  //
  // Offer selection
  //

  getGoalDateLabel({ fasterProgressDays } = {}) {
    const isMetric = this.isMetric()
    // Use already stored used data
    const { weightKg, weightLbs, targetWeightKg, targetWeightLbs, goalPaceKg, goalPaceLbs } =
      this.store.values

    const daysTillGoalDate = getDaysTillGoalDate(
      {
        weightKg,
        weightLbs,
        targetWeightKg,
        targetWeightLbs,
        goalPaceKg,
        goalPaceLbs,
      },
      isMetric,
    )

    // Use manual value for the faster progress days
    // or calculate automatically
    const fasterProgressDaysDiff =
      fasterProgressDays || getFasterProgressDaysDiff(this.store.values, isMetric)

    const goalDate = new Date()

    if (weightKg <= targetWeightKg || weightLbs <= targetWeightLbs) {
      // Weight gain scenario: show month from now
      goalDate.setDate(goalDate.getDate() + 30)
    } else {
      goalDate.setDate(goalDate.getDate() + daysTillGoalDate - fasterProgressDaysDiff)
    }

    return getLocalizedDateLabel(goalDate, true, true)
  },

  isOfferActive(offer) {
    const offerId = getOfferId(offer)
    const activeOfferId = this.activeOffer && getOfferId(this.activeOffer)
    return offerId === activeOfferId
  },

  isBestDeal(offer) {
    return this.store.pricing.isBestDeal({
      offer,
      offers: this.offers,
      isChance: this.store.values.exitIntent,
    })
  },

  getPriceDataValue(offer, dataKey) {
    return this.store.pricing.getPriceDataValue({ offer, dataKey })
  },

  getPricePerPeriodNoDiscount(offer) {
    return this.store.pricing.getPricePerPeriodNoDiscount({ offer })
  },

  getPriceNoDiscount(offer) {
    return this.store.pricing.getPriceNoDiscount({ offer })
  },

  getPrice(offer) {
    return this.store.pricing.getPrice({
      offer,
      isChance: this.store.values.exitIntent,
    })
  },

  getPriceDiscountOriginal(offer) {
    return this.store.pricing.getPriceDiscount({
      offer,
      isChance: false,
    })
  },

  getPriceDiscount(offer) {
    return this.store.pricing.getPriceDiscount({
      offer,
      isChance: this.store.values.exitIntent,
    })
  },

  getPricePerPeriod(offer) {
    return this.store.pricing.getPricePerPeriod({
      offer,
      isChance: this.store.values.exitIntent,
    })
  },

  renderPricePerPeriod(offer) {
    const price = this.getPricePerPeriod(offer)

    return this.store.pricing.addPricePeriod({
      price,
      period: offer?.customData?.period,
    })
  },

  getCurrency() {
    return pipelineConfig.currencyCode
  },

  getFuturePrice() {
    const offer = this.activeOffer
    const isChance = this.store.values.exitIntent

    return this.store.pricing.getFuturePrice({
      offer,
      isChance,
    })
  },

  //
  // Page UI
  //

  scrollToElement(id, trackingEventName, options) {
    if (trackingEventName) {
      this.store.internalTracker.trackClick({ clickName: trackingEventName })
    }

    const element = document.getElementById(id)
    if (!element) return

    const behavior = options?.behavior || "smooth"
    const block = options?.block || "start"

    element.scrollIntoView({ behavior, block })
  },

  getBmi() {
    return getBmi(this.store.values, this.isMetric())
  },

  getTargetBmi() {
    const { heightCm, heightFt, heightIn, targetWeightKg, targetWeightLbs } = this.store.values

    const values = {
      heightCm,
      heightFt,
      heightIn,
      weightKg: targetWeightKg,
      weightLbs: targetWeightLbs,
    }

    return getBmi(values, this.isMetric())
  },

  isMetric() {
    return this.store.values.measurementSystem === "metric"
  },

  getTargetWeightLoss() {
    const { weightKg, weightLbs, targetWeightKg, targetWeightLbs } = this.store.values

    const weight = this.isMetric() ? weightKg : weightLbs
    const targetWeight = this.isMetric() ? targetWeightKg : targetWeightLbs
    const delta = this.isMetric() ? 5 : kgToLbs(5)
    const diff = weight - targetWeight

    if (diff <= delta) {
      return Math.ceil(delta)
    } else {
      return Math.ceil(diff)
    }
  },

  isWeightGain() {
    return isWeightGain(this.store.values, this.isMetric())
  },

  getProjectedWeightLoss(months) {
    return getProjectedWeightLoss({
      values: this.store.values,
      isMetric: this.isMetric(),
      months: months,
    })
  },

  isTrial(offer = this.activeOffer) {
    return this.store.pricing.isOfferTrial(offer)
  },

  //

  getOfferMonths(offer = this.activeOffer) {
    return getOfferMonths(offer)
  },

  isOfferMultipleMonths(offer = this.activeOffer) {
    return getOfferMonths(offer) > 1
  },

  //

  getOfferWeeks(offer = this.activeOffer) {
    return getOfferWeeks(offer)
  },

  isOfferMultipleWeeks(offer = this.activeOffer) {
    return getOfferWeeks(offer) > 1
  },

  isPipeWeekTrial() {
    return isPipeWeekTrial(pipelineConfig.offers)
  },
})
